

import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.05); }
`;

const StyledErrorFallback = styled.main`
  height: 100vh;
  background-color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Box = styled.div`
  background-color: var(--gray-trans-color, #f3f3f398);
  border: 1px solid var(--light-gray-trans-color, #e4e4e435);
  border-radius: 16px;
  padding: 3rem 3.5rem;
  max-width: 480px;
  width: 100%;
  text-align: center;
  animation: ${fadeIn} 0.6s ease-out;
`;

const IconCircle = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(231, 117, 46, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  animation: ${pulse} 2.5s ease-in-out infinite;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--dark-gray-color, rgb(30, 30, 30));
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 1.5rem;
`;

const ErrorMessage = styled.div`
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 0.85rem 1rem;
  margin-bottom: 2rem;
  font-family: "Courier New", monospace;
  font-size: 0.8rem;
  color: #c0392b;
  word-break: break-word;
  max-height: 120px;
  overflow-y: auto;
  text-align: left;
`;

const RetryButton = styled.button`
  background: var(--orange-color, #e7752eb9);
  color: var(--whiteish-color, #fff);
  border: none;
  border-radius: 10px;
  padding: 0.75rem 2.2rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s, background-color 0.2s;
  box-shadow: 0 4px 16px rgba(231, 117, 46, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 24px rgba(231, 117, 46, 0.45);
  }

  &:active {
    transform: translateY(0);
  }
`;

function ErrorFallback({ error, resetErrorBoundary }) {
  return (
    <StyledErrorFallback>
      <Box>
        <IconCircle>
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#e7752e"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </IconCircle>
        <Title>Something went wrong</Title>
        <Subtitle>An unexpected error occurred. You can try again below.</Subtitle>
        <ErrorMessage>{error.message}</ErrorMessage>
        <RetryButton onClick={resetErrorBoundary}>Try Again</RetryButton>
      </Box>
    </StyledErrorFallback>
  );
}

export default ErrorFallback;
